import { Image } from "expo-image";
import { Link } from "expo-router";
import { Linking, Platform, ScrollView, StyleSheet, View } from "react-native";

import { GText } from "@/components/g-text";

const TEXT_COLOR = "#FEF7F3";
const APP_STORE_URL = "https://apps.apple.com/us/app/aglow-life/id6463022133";
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.aglow.abbey";
const VIDEO_URL =
  "https://customer-auxzz4hxwwu85pts.cloudflarestream.com/62ac685fb312781506a676db436f9a38/iframe?muted=true&preload=true&loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-auxzz4hxwwu85pts.cloudflarestream.com%2F62ac685fb312781506a676db436f9a38%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600&controls=false";

export default function HomePage() {
  if (Platform.OS !== "web") {
    return (
      <View style={styles.nativeContainer}>
        <GText style={styles.nativeText}>hello world</GText>
      </View>
    );
  }

  return (
    <ScrollView
      contentContainerStyle={styles.scrollContent}
      style={styles.container}
    >
      <View style={styles.pageWrapper}>
        {/* Hero */}
        <View style={styles.hero}>
          <View style={styles.logoContainer}>
            <Image
              contentFit="contain"
              source={require("../../assets/logo.svg")}
              style={styles.logo}
            />
          </View>
          <GText style={styles.tagline}>more social, less media</GText>
          <Link href="/e/" style={styles.eventsButton}>
            <GText style={styles.eventsButtonText}>see upcoming events</GText>
          </Link>
        </View>

        {/* Video */}
        <View style={styles.videoShowcase}>
          <View style={styles.videoContainer}>
            <iframe
              allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
              allowFullScreen
              loading="lazy"
              src={VIDEO_URL}
              style={{
                border: "none",
                borderRadius: 30,
                height: "100%",
                left: 0,
                position: "absolute",
                top: 0,
                transform: "scale(1.01)",
                width: "100%",
              }}
            />
          </View>
        </View>

        <StoreButtons />

        {/* About */}
        <View style={styles.about}>
          <GText style={styles.sectionTitle}>about aglow</GText>
          <GText style={styles.sectionDescription}>
            aglow is built for gathering. a place for people to share their
            lives and organize moments that turn neighborhoods into communities
          </GText>
          <View style={styles.featureCards}>
            <FeatureCard
              icon={require("../../assets/home/gather.svg")}
              text="attend an event nearby or create one of your own!"
              title="gather"
            />
            <FeatureCard
              icon={require("../../assets/home/discover.svg")}
              text="see what your neighbors are sharing and explore nearby events"
              title="discover"
            />
            <FeatureCard
              icon={require("../../assets/home/share.svg")}
              text="post a short video: coffee, pets, thoughts, and friends. share your world, unfiltered."
              title="share"
            />
          </View>
        </View>

        {/* Mission */}
        <View style={styles.mission}>
          <GText style={styles.sectionTitle}>our mission</GText>
          <GText style={styles.sectionDescription}>
            we built aglow to be what social media should have been: actually
            social. an easy way to meet people around you, discover what&apos;s
            happening nearby, and gather in real life. our mission is to connect
            neighbors and make gathering effortless. everyone&apos;s life is
            worth sharing. share yours locally. share yours on aglow.
          </GText>
          <View style={styles.valuesList}>
            <ValueItem
              icon={require("../../assets/home/unfiltered.svg")}
              text="no filters or ai-generated content in your feed: if it was posted on aglow, it really happened."
              title="unfiltered"
            />
            <ValueItem
              icon={require("../../assets/home/safe.svg")}
              text="your exact location is never shared. we actively moderate to ensure aglow remains safe and respectful."
              title="safe"
            />
            <ValueItem
              icon={require("../../assets/home/mindful.svg")}
              text="we measure success by events attended, not time spent on screen. aglow is built to get you offline and into your community."
              title="mindful"
            />
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <GText style={styles.footerTitle}>stay connected</GText>
          <GText
            onPress={() => Linking.openURL("mailto:hello@aglow.life")}
            style={styles.footerEmail}
          >
            hello@aglow.life
          </GText>

          <GText style={styles.footerCta}>meet your neighbors</GText>
          <GText style={styles.footerSubtitle}>
            download to join your local community!
          </GText>

          <StoreButtons style={styles.footerStoreButtons} />

          <View style={styles.footerLogo}>
            <Image
              contentFit="contain"
              source={require("../../assets/favicon.svg")}
              style={styles.footerLogoImage}
            />
          </View>

          <GText style={styles.copyright}>&copy; 2026 aglow life</GText>
        </View>
      </View>
    </ScrollView>
  );
}

function FeatureCard({
  icon,
  text,
  title,
}: {
  icon: string;
  text: string;
  title: string;
}) {
  return (
    <View style={styles.featureCard}>
      <Image contentFit="contain" source={icon} style={styles.featureIcon} />
      <View style={styles.featureContent}>
        <GText style={styles.featureTitle}>{title}</GText>
        <GText style={styles.featureText}>{text}</GText>
      </View>
    </View>
  );
}

function StoreButtons({ style }: { style?: View["props"]["style"] }) {
  return (
    <View style={[styles.storeButtons, style]}>
      <Link
        href={APP_STORE_URL as never}
        style={styles.storeLink}
        target="_blank"
      >
        <Image
          contentFit="contain"
          source={require("../../assets/download/app-store2.svg")}
          style={styles.storeImage}
        />
      </Link>
      <Link
        href={PLAY_STORE_URL as never}
        style={styles.storeLink}
        target="_blank"
      >
        <Image
          contentFit="contain"
          source={require("../../assets/download/google-play2.svg")}
          style={styles.storeImage}
        />
      </Link>
    </View>
  );
}

function ValueItem({
  icon,
  text,
  title,
}: {
  icon: string;
  text: string;
  title: string;
}) {
  return (
    <View style={styles.valueItem}>
      <Image contentFit="contain" source={icon} style={styles.valueIcon} />
      <View style={styles.valueContent}>
        <GText style={styles.valueTitle}>{title}</GText>
        <GText style={styles.valueText}>{text}</GText>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  about: {
    paddingVertical: 48,
  },
  container: {
    flex: 1,
  },
  copyright: {
    color: TEXT_COLOR,
    fontSize: 14,
    opacity: 0.6,
  },
  eventsButton: {
    backgroundColor: "rgba(0,0,0,0.8)",
    borderColor: "rgba(255,255,255,0.9)",
    borderRadius: 10,
    borderWidth: 1,
    marginTop: 16,
    paddingHorizontal: 24,
    paddingVertical: 10,
  },
  eventsButtonText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "600",
  },
  featureCard: {
    backgroundColor: "rgba(0, 0, 0, 0.30)",
    borderColor: TEXT_COLOR,
    borderRadius: 10,
    borderWidth: 1,
    flexDirection: "row",
    gap: 12,
    padding: 12,
  },
  featureCards: {
    gap: 12,
    paddingVertical: 8,
  },
  featureContent: {
    flex: 1,
    paddingLeft: 12,
  },
  featureIcon: {
    height: 24,
    width: 24,
  },
  featureText: {
    color: TEXT_COLOR,
    fontSize: 14,
    lineHeight: 17,
    opacity: 0.8,
  },
  featureTitle: {
    color: TEXT_COLOR,
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 4,
  },
  footer: {
    alignItems: "center",
    paddingBottom: 32,
    paddingTop: 48,
  },
  footerCta: {
    color: TEXT_COLOR,
    fontSize: 18,
    fontWeight: "600",
    marginBottom: 8,
  },
  footerEmail: {
    color: TEXT_COLOR,
    fontSize: 16,
    marginBottom: 32,
  },
  footerLogo: {
    marginBottom: 8,
    marginTop: 24,
  },
  footerLogoImage: {
    height: 60,
    width: 60,
  },
  footerStoreButtons: {
    backgroundColor: "transparent",
  },
  footerSubtitle: {
    color: TEXT_COLOR,
    fontSize: 16,
    marginBottom: 24,
    opacity: 0.8,
  },
  footerTitle: {
    color: TEXT_COLOR,
    fontSize: 18,
    fontWeight: "600",
    marginBottom: 8,
  },
  hero: {
    alignItems: "center",
    paddingBottom: 32,
    paddingTop: 64,
  },
  logo: {
    height: 60,
    width: 240,
  },
  logoContainer: {
    marginBottom: 16,
  },
  mission: {
    paddingVertical: 48,
  },
  nativeContainer: {
    alignItems: "center",
    flex: 1,
    justifyContent: "center",
  },
  nativeText: {
    color: TEXT_COLOR,
    fontSize: 24,
  },
  pageWrapper: {
    alignSelf: "center",
    maxWidth: 600,
    paddingHorizontal: 16,
    width: "100%",
  },
  scrollContent: {
    flexGrow: 1,
  },
  sectionDescription: {
    color: TEXT_COLOR,
    fontSize: 16,
    lineHeight: 26,
    marginBottom: 24,
    opacity: 0.9,
    textAlign: "center",
  },
  sectionTitle: {
    color: TEXT_COLOR,
    fontSize: 20,
    fontWeight: "600",
    marginBottom: 12,
    textAlign: "center",
  },
  storeButtons: {
    flexDirection: "row",
    gap: 12,
    justifyContent: "center",
  },
  storeImage: {
    height: 40,
    width: 135,
  },
  storeLink: {
    backgroundColor: "rgba(0,0,0,0.8)",
    borderColor: "rgba(255,255,255,0.9)",
    borderRadius: 10,
    borderWidth: 1,
    paddingHorizontal: 16,
    paddingVertical: 6,
  },
  tagline: {
    color: TEXT_COLOR,
    fontSize: 18,
    fontWeight: "600",
    opacity: 0.95,
  },
  valueContent: {
    flex: 1,
  },
  valueIcon: {
    height: 54,
    width: 54,
  },
  valueItem: {
    flexDirection: "row",
    gap: 16,
    maxWidth: 500,
  },
  valuesList: {
    alignItems: "center",
    gap: 32,
    marginTop: 32,
  },
  valueText: {
    color: TEXT_COLOR,
    fontSize: 14,
    lineHeight: 20,
    opacity: 0.9,
  },
  valueTitle: {
    color: TEXT_COLOR,
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 8,
  },
  videoContainer: {
    aspectRatio: 1206 / 2622,
    backgroundColor: "transparent",
    borderColor: "rgba(255, 255, 255, 0.9)",
    borderRadius: 30,
    borderWidth: 2,
    height: "80vh" as unknown as number,
    overflow: "hidden",
  },
  videoShowcase: {
    alignItems: "center",
    paddingBottom: 50,
    paddingVertical: 8,
  },
});
